import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ownerAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { 
  Building2, Calendar, Banknote, Clock, Users, CheckCircle, XCircle, 
  AlertCircle, ArrowRight, Loader2, MapPin, Camera 
} from 'lucide-react';
import { generateInitials } from '../lib/utils';

export default function OwnerDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await ownerAPI.getDashboard();
      setData(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden des Dashboards');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmBooking = async (bookingId) => {
    try {
      await ownerAPI.confirmBooking(bookingId);
      toast.success('Buchung bestätigt');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const handleRejectBooking = async (bookingId) => {
    try {
      await ownerAPI.rejectBooking(bookingId);
      toast.success('Buchung abgelehnt');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      pending: 'Ausstehend',
      confirmed: 'Bestätigt',
      completed: 'Abgeschlossen',
      rejected: 'Abgelehnt',
      cancelled: 'Storniert'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/profile" className="relative group">
              <Avatar className="h-16 w-16 border-2 border-tunis-blue/20">
                <AvatarImage src={user?.avatar_url} alt={user?.name} />
                <AvatarFallback className="bg-tunis-blue text-white text-xl font-bold">
                  {generateInitials(user?.name || 'O')}
                </AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Camera className="w-5 h-5 text-white" />
              </div>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-tunis-blue">Feldbesitzer Dashboard</h1>
              <p className="text-muted-foreground">Willkommen zurück, {user?.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/owner/fields">
              <Button variant="outline">Plätze verwalten</Button>
            </Link>
            <Link to="/owner/bookings">
              <Button className="btn-primary">Alle Buchungen</Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Meine Plätze</p>
                  <p className="text-2xl font-bold text-tunis-blue">{data?.total_fields || 0}</p>
                </div>
                <Building2 className="w-8 h-8 text-tunis-teal" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ausstehend</p>
                  <p className="text-2xl font-bold text-yellow-600">{data?.pending_bookings || 0}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Bestätigt</p>
                  <p className="text-2xl font-bold text-green-600">{data?.confirmed_bookings || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Einnahmen</p>
                  <p className="text-2xl font-bold text-tunis-teal">{(data?.total_earnings || 0).toFixed(2)} TND</p>
                </div>
                <Banknote className="w-8 h-8 text-tunis-teal" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Commission Info */}
        <Card className="bg-tunis-blue/5 border-tunis-blue/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ihre Provision</p>
                <p className="text-lg font-semibold">{((data?.commission_rate || 0.1) * 100).toFixed(0)}% Plattformgebühr</p>
                <p className="text-sm text-muted-foreground">Sie erhalten {(100 - (data?.commission_rate || 0.1) * 100).toFixed(0)}% jeder Buchung</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Aktuelles Guthaben</p>
                <p className="text-2xl font-bold text-tunis-teal">{(data?.wallet_balance || 0).toFixed(2)} TND</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Today's Bookings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Heutige Buchungen
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.todays_bookings?.length > 0 ? (
                <div className="space-y-3">
                  {data.todays_bookings.map((booking) => (
                    <div key={booking.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{booking.field_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {booking.start_time} - {booking.end_time} | {booking.user_name}
                        </p>
                      </div>
                      {statusBadge(booking.status)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">Keine Buchungen heute</p>
              )}
            </CardContent>
          </Card>

          {/* Pending Bookings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-500" />
                Ausstehende Anfragen
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.recent_bookings?.filter(b => b.status === 'pending').length > 0 ? (
                <div className="space-y-3">
                  {data.recent_bookings.filter(b => b.status === 'pending').slice(0, 5).map((booking) => (
                    <div key={booking.id} className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">{booking.field_name}</p>
                        <p className="text-sm font-semibold text-tunis-teal">{(booking.total_price || 0).toFixed(2)} TND</p>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {booking.date} | {booking.start_time} - {booking.end_time}
                      </p>
                      <p className="text-sm mb-3">
                        <Users className="w-4 h-4 inline mr-1" />
                        {booking.user_name} {booking.user_phone && `(${booking.user_phone})`}
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => handleConfirmBooking(booking.id)}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Bestätigen
                        </Button>
                        <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleRejectBooking(booking.id)}>
                          <XCircle className="w-4 h-4 mr-1" /> Ablehnen
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">Keine ausstehenden Anfragen</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* My Fields */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Meine Plätze
            </CardTitle>
            <Link to="/owner/fields">
              <Button variant="ghost" size="sm">Alle anzeigen <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </Link>
          </CardHeader>
          <CardContent>
            {data?.fields?.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.fields.slice(0, 6).map((field) => (
                  <div key={field.id} className="p-4 border rounded-lg hover:border-tunis-teal transition-colors">
                    <h3 className="font-semibold">{field.name}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{field.location}</p>
                    <p className="text-lg font-bold text-tunis-teal">{field.price_per_hour} TND/Std.</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-muted-foreground mb-4">Sie haben noch keine Plätze</p>
                <Link to="/owner/fields">
                  <Button className="btn-primary">Ersten Platz hinzufügen</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
